var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, } from "react";
import cn from "classnames";
import { useIcon } from "@react-md/icon";
import { bem } from "@react-md/utils";
import FloatingLabel from "../label/FloatingLabel";
import TextFieldContainer from "../text-field/TextFieldContainer";
import useValuedState from "../text-field/useValuedState";
import useFocusState from "../useFocusState";
var block = bem("rmd-native-select");
var container = bem("rmd-native-select-container");
/**
 * This component is used to render a native `<select>` element with the text
 * field theme styles. This component is great to use for native behavior and
 * full accessibility.
 */
var NativeSelect = forwardRef(function NativeSelect(_a, ref) {
    var style = _a.style, className = _a.className, labelStyle = _a.labelStyle, labelClassName = _a.labelClassName, selectStyle = _a.selectStyle, selectClassName = _a.selectClassName, propIcon = _a.icon, _b = _a.theme, theme = _b === void 0 ? "outline" : _b, _c = _a.dense, dense = _c === void 0 ? false : _c, _d = _a.inline, inline = _d === void 0 ? false : _d, _e = _a.error, error = _e === void 0 ? false : _e, _f = _a.disabled, disabled = _f === void 0 ? false : _f, label = _a.label, propOnBlur = _a.onBlur, propOnFocus = _a.onFocus, propOnChange = _a.onChange, containerRef = _a.containerRef, isLeftAddon = _a.isLeftAddon, isRightAddon = _a.isRightAddon, leftChildren = _a.leftChildren, rightChildren = _a.rightChildren, _g = _a.underlineDirection, underlineDirection = _g === void 0 ? "left" : _g, children = _a.children, props = __rest(_a, ["style", "className", "labelStyle", "labelClassName", "selectStyle", "selectClassName", "icon", "theme", "dense", "inline", "error", "disabled", "label", "onBlur", "onFocus", "onChange", "containerRef", "isLeftAddon", "isRightAddon", "leftChildren", "rightChildren", "underlineDirection", "children"]);
    var id = props.id, value = props.value, defaultValue = props.defaultValue, multiple = props.multiple;
    var underline = theme === "underline" || theme === "filled";
    var icon = useIcon("dropdown", propIcon);
    var _h = useFocusState({
        onBlur: propOnBlur,
        onFocus: propOnFocus,
    }), focused = _h[0], onFocus = _h[1], onBlur = _h[2];
    var _j = useValuedState({
        value: value,
        defaultValue: defaultValue,
        onChange: propOnChange,
    }), valued = _j[0], onChange = _j[1];
    return (React.createElement(TextFieldContainer, { style: style, className: cn(container({
            multi: multiple,
            padded: multiple && label,
        }), className), ref: containerRef, theme: theme, error: error, active: focused, label: !!label, dense: dense, inline: inline, disabled: disabled, isLeftAddon: isLeftAddon, isRightAddon: isRightAddon, leftChildren: leftChildren, rightChildren: multiple && rightChildren, underlineDirection: underlineDirection },
        React.createElement(FloatingLabel, { style: labelStyle, className: cn(block("label"), labelClassName), htmlFor: id, error: error, active: valued && focused, valued: valued, floating: valued || multiple, dense: dense, disabled: disabled }, label),
        React.createElement("select", __assign({}, props, { ref: ref, style: selectStyle, className: cn(block({
                icon: icon,
                multi: multiple,
                "label-underline": label && underline,
                "placeholder-underline": !label && underline,
                floating: label && theme !== "none",
            }), selectClassName), disabled: disabled, onFocus: onFocus, onBlur: onBlur, onChange: onChange }), children),
        !multiple && icon && React.createElement("span", { className: block("icon") }, icon)));
});
if (process.env.NODE_ENV !== "production") {
    try {
        var PropTypes = require("prop-types");
        NativeSelect.propTypes = {
            id: PropTypes.string.isRequired,
            style: PropTypes.object,
            className: PropTypes.string,
            labelStyle: PropTypes.object,
            labelClassName: PropTypes.string,
            selectStyle: PropTypes.object,
            selectClassName: PropTypes.string,
            icon: PropTypes.node,
            value: PropTypes.string,
            defaultValue: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.arrayOf(PropTypes.string),
            ]),
            theme: PropTypes.oneOf(["none", "underline", "filled", "outline"]),
            dense: PropTypes.bool,
            error: PropTypes.bool,
            inline: PropTypes.bool,
            disabled: PropTypes.bool,
            underlineDirection: PropTypes.oneOf(["left", "right"]),
            isLeftAddon: PropTypes.bool,
            leftChildren: PropTypes.node,
            rightChildren: PropTypes.node,
            isRightAddon: PropTypes.bool,
            multiple: PropTypes.bool,
            size: PropTypes.number,
            children: PropTypes.node,
            label: PropTypes.node,
            onBlur: PropTypes.func,
            onFocus: PropTypes.func,
            onChange: PropTypes.func,
            containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
        };
    }
    catch (e) { }
}
export default NativeSelect;
//# sourceMappingURL=NativeSelect.js.map